<template>
  <div class="gift-recipient-page">
    <el-card shadow="never">
      <template slot="header">
        <div class="card-head">
          <div>
            <span class="title"><i class="el-icon-present" style="color: #409eff; margin-right: 6px;"></i>礼品名单管理</span>
            <span class="muted" style="margin-left: 12px; font-size: 13px;">维护可领取礼品/产品的人员名单（支持客户姓名模糊检索、批量导入与导出）</span>
          </div>
          <div class="head-tools">
            <el-button v-if="$hasPerm('func:gift-recipient:import')" type="warning" size="small" icon="el-icon-upload2" @click="openImportDialog">批量导入</el-button>
            <el-button type="success" size="small" icon="el-icon-download" :loading="exporting" @click="handleExport">导出 Excel</el-button>
            <el-button
              v-if="$hasPerm('func:gift-recipient:unfreeze')"
              type="warning"
              plain
              size="small"
              icon="el-icon-unlock"
              :disabled="!selectedDormantRows.length"
              @click="handleBatchUnfreeze"
            >
              批量解禁{{ selectedDormantRows.length ? ' (' + selectedDormantRows.length + ')' : '' }}
            </el-button>
            <el-button
              v-if="$hasPerm('func:gift-recipient:edit')"
              type="primary"
              size="small"
              icon="el-icon-collection-tag"
              :disabled="!selectedRows.length"
              @click="openBatchCategoryDialog"
            >
              批量设置身份{{ selectedRows.length ? ' (' + selectedRows.length + ')' : '' }}
            </el-button>
            <el-button
              v-if="$hasPerm('func:gift-recipient:delete')"
              type="danger"
              size="small"
              icon="el-icon-delete"
              :disabled="!selectedRows.length"
              @click="handleBatchDelete"
            >
              批量删除{{ selectedRows.length ? ' (' + selectedRows.length + ')' : '' }}
            </el-button>
          </div>
        </div>
      </template>

      <!-- 搜索筛选栏与右侧新建 -->
      <div class="search-row-wrap">
        <el-form :inline="true" size="small" class="search-bar" @submit.native.prevent="handleSearch">
          <el-form-item label="客户姓名">
            <el-input
              v-model="searchName"
              placeholder="客户、配偶姓名"
              clearable
              prefix-icon="el-icon-search"
              style="width: 220px"
              @clear="handleSearch"
              @keyup.enter.native="handleSearch"
            >
            </el-input>
          </el-form-item>

          <el-form-item label="客户身份">
            <el-select
              v-model="searchGiftTypeId"
              placeholder="全部身份"
              clearable
              style="width: 160px"
              @change="handleSearch"
            >
              <el-option
                v-for="t in giftTypeList"
                :key="t.id"
                :label="t.customerCategoryName || t.name"
                :value="t.id"
              ></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="开发人">
            <el-input
              v-model="searchDeveloper"
              placeholder="开发人"
              clearable
              style="width: 160px"
              @clear="handleSearch"
              @keyup.enter.native="handleSearch"
            >
            </el-input>
          </el-form-item>

          <el-form-item label="客户负责人">
            <el-select
              v-model="searchManager"
              placeholder="全部负责人"
              clearable
              filterable
              style="width: 160px"
              @change="handleSearch"
            >
              <el-option
                v-for="u in salesmanList"
                :key="u.id"
                :label="u.name || u.username"
                :value="u.name || u.username"
              ></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="休眠状态">
            <el-select
              v-model="searchDormantStatus"
              placeholder="全部状态"
              clearable
              style="width: 120px"
              @change="handleSearch"
            >
              <el-option label="正常" :value="0"></el-option>
              <el-option label="礼品休眠" :value="1"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
          </el-form-item>
        </el-form>

        <div class="search-row-right">
          <el-button v-if="$hasPerm('func:gift-recipient:create')" type="primary" size="small" icon="el-icon-plus" @click="openDialog()">新建名单</el-button>
        </div>
      </div>

      <!-- 名单数据表格 -->
      <el-table
        :data="list"
        border
        stripe
        size="small"
        v-loading="loading"
        @selection-change="handleSelectionChange"
        :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
      >
        <el-table-column type="selection" width="45" align="center"></el-table-column>
        <el-table-column type="index" label="序号" width="65" align="center" :index="calcIndex"></el-table-column>
        <el-table-column prop="customerName" label="客户姓名" min-width="160" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <el-link type="primary" :underline="false" style="font-weight: 600; font-size: 13px;" @click="jumpToReport(row.customerName, row)">
              {{ row.customerName }}
            </el-link>
            <el-tooltip v-if="row.customerId" content="已与【客户管理】档案强绑定（改名将双向自动同步）" placement="top">
              <el-tag size="mini" type="success" effect="plain" style="margin-left: 6px; font-size: 11px; height: 20px; line-height: 18px; padding: 0 4px;">
                <i class="el-icon-link"></i> 关联档案
              </el-tag>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="客户身份" min-width="170" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <div v-if="row.giftTypeTagList && row.giftTypeTagList.length" style="display: flex; flex-wrap: wrap; gap: 4px;">
              <el-tag
                v-for="tag in row.giftTypeTagList"
                :key="tag.id"
                size="mini"
                :color="tag.color || '#409EFF'"
                style="color: #fff; border-color: transparent; font-weight: 500;"
              >
                {{ tag.name }}
              </el-tag>
            </div>
            <span v-else-if="row.giftTypeNames" class="muted">{{ row.giftTypeNames }}</span>
            <span v-else class="muted">未设置身份</span>
          </template>
        </el-table-column>
        <el-table-column prop="developer" label="开发人" min-width="120" align="center" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <el-link v-if="row.developer" type="primary" :underline="false" @click="jumpToReport(null, { developer: row.developer })">
              {{ row.developer }}
            </el-link>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="manager" label="客户负责人" min-width="120" align="center" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <el-link v-if="row.manager" type="primary" :underline="false" @click="jumpToReport(null, { manager: row.manager })">
              {{ row.manager }}
            </el-link>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="115" align="center">
          <template slot-scope="{ row }">
            <el-tooltip v-if="row.dormantStatus === 1" placement="top">
              <div slot="content">
                <div><strong>已处于礼品休眠状态</strong></div>
                <div v-if="row.dormantTime">休眠时间：{{ row.dormantTime }}</div>
                <div v-if="row.lastGiftApplyTime">最近领礼品：{{ row.lastGiftApplyTime }}</div>
                <div v-if="row.unfreezeTime">上次解禁：{{ row.unfreezeTime }}</div>
                <div style="margin-top: 4px; color: #E6A23C;">提示：解禁后方可重新申报礼品</div>
              </div>
              <el-tag size="mini" type="danger" effect="light" style="cursor: pointer;">
                <i class="el-icon-moon"></i> 礼品休眠
              </el-tag>
            </el-tooltip>
            <el-tag v-else size="mini" type="success" effect="light">
              <i class="el-icon-circle-check"></i> 正常
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="130" align="center">
          <template slot-scope="{ row }">
            <span>{{ formatTime(row.createTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="$hasPerm('func:gift-recipient:edit') || $hasPerm('func:gift-recipient:delete') || $hasPerm('func:gift-recipient:unfreeze')" label="操作" width="300" align="center" fixed="right">
          <template slot-scope="{ row }">
            <div class="action-btn-group">
              <el-button
                v-if="row.dormantStatus === 1 && $hasPerm('func:gift-recipient:unfreeze')"
                type="text"
                size="small"
                icon="el-icon-unlock"
                style="color: #E6A23C; font-weight: 600;"
                @click="handleUnfreeze(row)"
              >
                解禁
              </el-button>
              <el-button v-if="$hasPerm('func:gift-recipient:edit')" type="text" size="small" icon="el-icon-edit" @click="openDialog(row)">编辑</el-button>
              <el-button v-if="$hasPerm('func:gift-recipient:delete')" type="text" size="small" icon="el-icon-delete" style="color: #F56C6C;" @click="handleDelete(row)">删除</el-button>
              <el-button type="text" size="small" icon="el-icon-s-data" @click="jumpToReport(row.customerName, row)">去报表</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-wrap">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="size"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
        >
        </el-pagination>
      </div>
    </el-card>

    <!-- 新建 / 编辑弹窗 -->
    <el-dialog
      :title="dialogForm.id ? '编辑礼品名单' : '新建礼品名单'"
      :visible.sync="dialogVisible"
      width="480px"
      :close-on-click-modal="false"
      @closed="resetDialogForm"
    >
      <el-form ref="dialogFormRef" :model="dialogForm" :rules="rules" label-width="95px" size="small">
        <el-alert
          v-if="dialogForm.id && dialogForm.customerId"
          type="success"
          :closable="false"
          show-icon
          style="margin-bottom: 14px; padding: 6px 12px;"
          title="已绑定客户档案：修改客户姓名/人员将自动同步至【客户管理】"
        ></el-alert>
        <el-form-item label="客户姓名" prop="customerName">
          <el-input v-model="dialogForm.customerName" placeholder="请输入客户姓名（如：张三 或 张三、李四）" clearable></el-input>
        </el-form-item>
        <el-form-item label="客户身份" prop="giftTypeIdList">
          <el-select v-model="dialogForm.giftTypeIdList" multiple placeholder="请选择客户身份（可多选）" style="width: 100%">
            <el-option v-for="t in giftTypeList" :key="t.id" :label="t.customerCategoryName || t.name" :value="t.id">
              <span style="float: left">{{ t.customerCategoryName || t.name }}</span>
              <span style="float: right; color: #8492a6; font-size: 12px">{{ t.name }}</span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="开发人" prop="developer">
          <el-input v-model="dialogForm.developer" placeholder="请输入开发人姓名" clearable></el-input>
        </el-form-item>
        <el-form-item label="客户负责人" prop="manager">
          <el-select
            v-model="dialogForm.manager"
            filterable
            clearable
            placeholder="请选择业务员"
            style="width: 100%"
          >
            <el-option
              v-for="u in salesmanList"
              :key="u.id"
              :label="u.name || u.username"
              :value="u.name || u.username"
            ></el-option>
            <!-- 历史/停用人员兼容回显 -->
            <el-option
              v-if="dialogForm.manager && !salesmanList.some(u => (u.name || u.username) === dialogForm.manager)"
              :key="'legacy-' + dialogForm.manager"
              :label="dialogForm.manager + '（历史记录）'"
              :value="dialogForm.manager"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="submitting" @click="handleSubmit">确定</el-button>
      </div>
    </el-dialog>

    <!-- 批量设置客户身份弹窗 -->
    <el-dialog
      title="批量设置客户身份"
      :visible.sync="batchCategoryDialogVisible"
      width="480px"
      :close-on-click-modal="false"
    >
      <div style="margin-bottom: 14px; font-size: 13px; color: #606266;">
        已勾选 <span style="color: #409eff; font-weight: bold;">{{ selectedRows.length }}</span> 位客户，请选择要批量分配的客户身份（可多选，不选则清空身份）：
      </div>
      <el-form label-width="95px" size="small">
        <el-form-item label="客户身份">
          <el-select v-model="batchCategoryIds" multiple placeholder="请选择客户身份（可多选）" style="width: 100%">
            <el-option v-for="t in giftTypeList" :key="t.id" :label="t.customerCategoryName || t.name" :value="t.id">
              <span style="float: left">{{ t.customerCategoryName || t.name }}</span>
              <span style="float: right; color: #8492a6; font-size: 12px">{{ t.name }}</span>
            </el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="batchCategoryDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="batchSettingCategory" @click="handleConfirmBatchCategory">确定保存</el-button>
      </div>
    </el-dialog>

    <!-- 批量导入弹窗 -->
    <el-dialog
      title="批量导入礼品名单"
      :visible.sync="importDialogVisible"
      width="500px"
      :close-on-click-modal="false"
      @closed="resetImport"
    >
      <div style="margin-bottom: 16px;">
        <p style="font-size: 13px; color: #606266; margin-bottom: 10px; line-height: 1.6;">
          1. 请先下载标准 Excel 导入模板，按列填入<strong>客户姓名、客户身份（选填）、开发人、客户负责人</strong>。<br/>
          2. 客户姓名支持输入单人或合称（如：<code>张三</code> 或 <code>张三、李四</code>）。<br/>
          3. 上传文件支持 <code>.xlsx</code> / <code>.xls</code> 格式。
        </p>
        <el-button type="text" icon="el-icon-download" @click="handleDownloadTemplate">点击下载《礼品名单导入模板.xlsx》</el-button>
      </div>

      <el-upload
        ref="uploadRef"
        drag
        action=""
        :auto-upload="false"
        :limit="1"
        accept=".xlsx, .xls"
        :on-change="handleFileChange"
        :on-remove="handleFileRemove"
        style="text-align: center;"
      >
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">将 Excel 文件拖到此处，或<em>点击上传</em></div>
        <div class="el-upload__tip" slot="tip">仅支持单次上传 1 个 Excel 文件</div>
      </el-upload>

      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="importDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="importing" :disabled="!importFile" @click="handleDoImport">开始导入</el-button>
      </div>
    </el-dialog>

    <!-- 弹窗 1：礼品名单去重确认弹窗 -->
    <el-dialog
      :title="importDirectList.length > 0 ? '导入预检确认（部分正常导入 / 部分需去重）' : '发现已存在的客户（无需重复导入）'"
      :visible.sync="dedupeDialogVisible"
      width="780px"
      :close-on-click-modal="false"
      append-to-body
    >
      <!-- 汇总状态栏（包含正常导入客户与去重客户） -->
      <div v-if="importDirectList.length > 0" style="margin-bottom: 14px; padding: 12px 14px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 13px;">
        <div style="font-weight: 600; color: #1e293b; margin-bottom: 8px;">
          本次表格共识别到 <span style="color: #409eff; font-size: 15px; font-weight: bold;">{{ importDirectList.length + giftDedupeList.length + customerAlignList.length }}</span> 位客户：
        </div>
        
        <!-- 正常导入新客户 -->
        <div style="padding: 8px 12px; background: #f0f9eb; border: 1px solid #c2e7b0; border-radius: 4px; margin-bottom: 8px;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="color: #67c23a; font-weight: 600;">
              <i class="el-icon-circle-check" style="font-size: 15px; vertical-align: middle;"></i>
              正常导入新客户（{{ importDirectList.length }} 位）：
              <span style="color: #529b2e; font-weight: normal;">无重复记录，点击下方【确认】后将<strong>直接正常导入</strong>。</span>
            </span>
            <el-button type="text" size="mini" @click="showDirectDetail = !showDirectDetail" style="padding: 0; color: #67c23a;">
              {{ showDirectDetail ? '收起正常名单 ▲' : '查看正常名单 ▼' }}
            </el-button>
          </div>
          <div v-if="showDirectDetail" style="margin-top: 6px; max-height: 90px; overflow-y: auto; background: #fff; padding: 6px 10px; border: 1px dashed #b3e19d; border-radius: 4px; font-size: 12px; line-height: 1.8;">
            <span v-for="(item, idx) in importDirectList" :key="idx" style="display: inline-block; margin: 2px 6px 2px 0; background: #f0f9eb; padding: 1px 6px; border-radius: 3px; color: #303133;">
              {{ item.customerName }}<span v-if="item.developer || item.manager" style="color: #909399; font-size: 11px;">（{{ item.developer || '-' }}/{{ item.manager || '-' }}）</span>
            </span>
          </div>
        </div>

        <!-- 需确认去重客户 -->
        <div style="padding: 8px 12px; background: #fdf6ec; border: 1px solid #f5dab1; border-radius: 4px;">
          <span style="color: #e6a23c; font-weight: 600;">
            <i class="el-icon-warning-outline" style="font-size: 15px; vertical-align: middle;"></i>
            已存在客户（{{ giftDedupeList.length }} 位）：
            <span style="color: #b88230; font-weight: normal;">名单中已有相同记录，已默认设为<strong>【无需导入】</strong>（跳过，原档案保持不变）；如需更新已有信息可切换为【覆盖更新】。</span>
          </span>
        </div>
      </div>

      <!-- 全量重复时的简要提示 -->
      <el-alert
        v-else
        type="warning"
        :closable="false"
        show-icon
        style="margin-bottom: 14px;"
      >
        <template slot="title">
          <span style="font-size: 13px; font-weight: bold;">
            本次表格中的 {{ giftDedupeList.length }} 位客户均已在礼品名单中存在，无需重复导入
          </span>
        </template>
        <div style="font-size: 12px; margin-top: 4px; color: #606266;">
          默认已选择「无需导入」（跳过重复客户，保持原档案不变）；如需用本次表格内容更新已有信息，请选择「覆盖更新」。
        </div>
      </el-alert>

      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
        <span style="font-size: 13px; color: #606266; font-weight: 600;">已存在客户列表（共 {{ giftDedupeList.length }} 位，请确认处理方式）：</span>
        <div>
          <el-button size="mini" type="warning" plain @click="setAllDedupeAction('skip')">全部设为【无需导入】</el-button>
          <el-button size="mini" type="primary" plain @click="setAllDedupeAction('overwrite')">全部设为【覆盖更新】</el-button>
        </div>
      </div>

      <el-table :data="giftDedupeList" border stripe size="mini" max-height="360" :header-cell-style="{ background: '#f5f7fa', color: '#606266' }">
        <el-table-column type="index" label="序号" width="50" align="center"></el-table-column>
        <el-table-column prop="customerName" label="本次导入姓名" min-width="130">
          <template slot-scope="{ row }">
            <span style="font-weight: 600; color: #303133;">{{ row.customerName }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="matchedName" label="已有姓名" min-width="130">
          <template slot-scope="{ row }">
            <span style="color: #e6a23c; font-weight: 600;">{{ row.matchedName }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="giftTypeNames" label="客户身份" width="120" align="center" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span v-if="row.giftTypeNames" style="color: #409EFF; font-weight: 500;">{{ row.giftTypeNames }}</span>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="developer" label="开发人" width="100" align="center"></el-table-column>
        <el-table-column prop="manager" label="客户负责人" width="100" align="center"></el-table-column>
        <el-table-column label="处理选择" width="190" align="center">
          <template slot-scope="{ row }">
            <el-radio-group v-model="row.action" size="mini">
              <el-radio-button label="skip">无需导入</el-radio-button>
              <el-radio-button label="overwrite">覆盖更新</el-radio-button>
            </el-radio-group>
          </template>
        </el-table-column>
      </el-table>

      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="dedupeDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" @click="handleDedupeConfirm">
          {{ customerAlignList.length > 0 ? '确定并继续下一步' : (importDirectList.length > 0 ? `确认（正常导入 ${importDirectList.length} 位）` : '确认') }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 弹窗 2：客户管理合称对齐确认弹窗 -->
    <el-dialog
      title="发现客户管理合称档案 - 请确认"
      :visible.sync="alignDialogVisible"
      width="780px"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-alert
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom: 14px;"
      >
        <template slot="title">
          <span style="font-size: 13px; font-weight: bold;">
            检测到以下 {{ customerAlignList.length }} 位客户在【客户管理】中已有合称档案
          </span>
        </template>
        <div style="font-size: 12px; margin-top: 4px; color: #606266;">
          您可以选择「合并为合称」（同步更新客户管理与礼品名单名称），或选择「独立保存」（创建新的独立名单）。
        </div>
      </el-alert>

      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
        <span style="font-size: 13px; color: #606266; font-weight: 600;">合称档案列表：</span>
        <div>
          <el-button size="mini" type="primary" plain @click="setAllAlignAction('merge')">全部设为【合并为合称】</el-button>
          <el-button size="mini" type="info" plain @click="setAllAlignAction('create')">全部设为【独立保存】</el-button>
        </div>
      </div>

      <el-table :data="customerAlignList" border stripe size="mini" max-height="360" :header-cell-style="{ background: '#f5f7fa', color: '#606266' }">
        <el-table-column type="index" label="序号" width="50" align="center"></el-table-column>
        <el-table-column prop="customerName" label="本次导入姓名" min-width="130">
          <template slot-scope="{ row }">
            <span style="font-weight: 600; color: #303133;">{{ row.customerName }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="matchedName" label="客户管理已有姓名" min-width="140">
          <template slot-scope="{ row }">
            <span style="color: #409eff; font-weight: 600;">{{ row.matchedName }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="giftTypeNames" label="客户身份" width="120" align="center" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span v-if="row.giftTypeNames" style="color: #409EFF; font-weight: 500;">{{ row.giftTypeNames }}</span>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="developer" label="开发人" width="100" align="center"></el-table-column>
        <el-table-column prop="manager" label="业务员(负责人)" width="100" align="center"></el-table-column>
        <el-table-column label="处理选择" width="190" align="center">
          <template slot-scope="{ row }">
            <el-radio-group v-model="row.action" size="mini">
              <el-radio-button label="merge">合并为合称</el-radio-button>
              <el-radio-button label="create">独立保存</el-radio-button>
            </el-radio-group>
          </template>
        </el-table-column>
      </el-table>

      <!-- 正常导入新客户提示 -->
      <div v-if="importDirectList.length > 0" style="margin-bottom: 12px; padding: 6px 12px; background: #f0f9eb; border: 1px solid #c2e7b0; border-radius: 4px; font-size: 12px; color: #67c23a;">
        <i class="el-icon-circle-check"></i> 本次无冲突的 <strong>{{ importDirectList.length }}</strong> 位新客户将在确认后一并正常导入。
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="alignDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="importConfirming" @click="handleFinalImport">
          {{ importDirectList.length > 0 ? `确认并执行导入（含正常导入 ${importDirectList.length} 位）` : '确认并执行导入' }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { giftRecipientApi, giftTypeApi, userApi } from '../api/api'

export default {
  name: 'GiftRecipient',
  data () {
    return {
      loading: false,
      exporting: false,
      importing: false,
      submitting: false,
      list: [],
      total: 0,
      page: 1,
      size: 20,

      // 搜索项
      searchName: '',
      searchGiftTypeId: null,
      searchDeveloper: '',
      searchManager: '',
      searchDormantStatus: null,

      selectedRows: [],
      giftTypeList: [],
      salesmanList: [],

      // 批量设置身份
      batchCategoryDialogVisible: false,
      batchCategoryIds: [],
      batchSettingCategory: false,

      // 新建/编辑弹窗
      dialogVisible: false,
      dialogForm: {
        id: null,
        customerId: null,
        customerName: '',
        developer: '',
        manager: '',
        giftTypeIdList: []
      },
      rules: {
        customerName: [{ required: true, message: '请输入客户姓名', trigger: 'blur' }],
        developer: [{ required: true, message: '请输入开发人姓名', trigger: 'blur' }],
        manager: [{ required: true, message: '请选择客户负责人', trigger: 'change' }]
      },

      // 导入弹窗
      importDialogVisible: false,
      importFile: null,

      // 导入分步确认弹窗
      dedupeDialogVisible: false,
      giftDedupeList: [],
      alignDialogVisible: false,
      customerAlignList: [],
      importDirectList: [],
      showDirectDetail: false,
      importConfirming: false
    }
  },
  computed: {
    selectedDormantRows () {
      return (this.selectedRows || []).filter(r => r.dormantStatus === 1)
    }
  },
  created () {
    this.fetchData()
    this.fetchGiftTypes()
    this.loadSalesmen()
  },
  methods: {
    loadSalesmen () {
      userApi.getUserList().then(res => {
        const list = Array.isArray(res.data) ? res.data : []
        this.salesmanList = list.filter(u => (u.role === 'salesman' || u.roleId === 3) && u.status !== 0)
      }).catch(() => {
        this.salesmanList = []
      })
    },
    fetchGiftTypes () {
      giftTypeApi.getGiftTypeList(1).then(res => {
        this.giftTypeList = Array.isArray(res.data) ? res.data : []
      }).catch(() => { this.giftTypeList = [] })
    },
    openBatchCategoryDialog () {
      if (!this.selectedRows.length) return
      this.batchCategoryIds = []
      this.batchCategoryDialogVisible = true
    },
    handleConfirmBatchCategory () {
      if (!this.selectedRows.length) return
      this.batchSettingCategory = true
      const ids = this.selectedRows.map(r => r.id)
      giftRecipientApi.batchSetGiftTypes(ids, this.batchCategoryIds).then(res => {
        if (res.data && res.data.success !== false) {
          this.$message.success(res.data.message || '批量设置身份成功')
          this.batchCategoryDialogVisible = false
          this.fetchData()
        } else {
          this.$message.error((res.data && res.data.message) || '批量设置失败')
        }
      }).catch(err => {
        this.$message.error('操作失败：' + (err.message || '网络错误'))
      }).finally(() => {
        this.batchSettingCategory = false
      })
    },
    calcIndex (idx) {
      return (this.page - 1) * this.size + idx + 1
    },
    formatTime (t) {
      if (!t) return '-'
      return t.substring(0, 10)
    },
    jumpToReport (name, row) {
      const query = {
        type: '礼品',
        dateType: 'day'
      }
      if (name) {
        query.customer = name
      }
      if (row) {
        if (row.developer && row.developer.trim()) {
          query.developer = row.developer.trim()
        }
        if (row.manager && row.manager.trim()) {
          query.manager = row.manager.trim()
        }
      }
      this.$router.push({
        path: '/report',
        query
      })
    },
    scopeParams () {
      const me = (function () { try { return JSON.parse(sessionStorage.getItem('user') || '{}') } catch (e) { return {} } })()
      const isSuper = me && (me.role === 'super_admin' || me.role === '超级管理员' || Number(me.roleId) === 1)
      const isAdmin = me && !isSuper && (me.role === 'admin' || Number(me.roleId) === 2)
      const isSales = me && !isSuper && !isAdmin && Number(me.roleId) === 3
      if (isAdmin && me.id) return { mineForUserId: me.id }
      if (isSales && me.id) return { salesmanUserId: me.id }
      return {}
    },
    fetchData () {
      this.loading = true
      const params = Object.assign({
        page: this.page,
        size: this.size,
        name: this.searchName.trim() || undefined,
        developer: this.searchDeveloper.trim() || undefined,
        manager: this.searchManager.trim() || undefined,
        giftTypeId: this.searchGiftTypeId || undefined,
        dormantStatus: (this.searchDormantStatus !== null && this.searchDormantStatus !== undefined && this.searchDormantStatus !== '') ? this.searchDormantStatus : undefined
      }, this.scopeParams())
      giftRecipientApi.getPage(params).then(res => {
        const d = res.data || {}
        this.list = d.records || []
        this.total = d.total || 0
      }).catch(err => {
        console.error(err)
        this.$message.error('获取名单失败')
      }).finally(() => {
        this.loading = false
      })
    },
    handleSearch () {
      this.page = 1
      this.fetchData()
    },
    handleReset () {
      this.searchName = ''
      this.searchGiftTypeId = null
      this.searchDeveloper = ''
      this.searchManager = ''
      this.searchDormantStatus = null
      this.page = 1
      this.fetchData()
    },
    handleSelectionChange (val) {
      this.selectedRows = val
    },
    handleSizeChange (v) {
      this.size = v
      this.page = 1
      this.fetchData()
    },
    handleCurrentChange (v) {
      this.page = v
      this.fetchData()
    },

    // 新增 / 编辑
    openDialog (row) {
      this.loadSalesmen()
      if (row) {
        this.dialogForm = {
          id: row.id,
          customerId: row.customerId || null,
          customerName: row.customerName || '',
          developer: row.developer || '',
          manager: row.manager || '',
          giftTypeIdList: row.giftTypeIdList ? [...row.giftTypeIdList] : []
        }
      } else {
        const me = (function () { try { return JSON.parse(sessionStorage.getItem('user') || '{}') } catch (e) { return {} } })()
        const isSales = me && (me.role === 'salesman' || Number(me.roleId) === 3)
        const defaultManager = (isSales && (me.name || me.username)) ? (me.name || me.username) : ''
        this.dialogForm = {
          id: null,
          customerId: null,
          customerName: '',
          developer: '',
          manager: defaultManager,
          giftTypeIdList: []
        }
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.dialogFormRef) this.$refs.dialogFormRef.clearValidate()
      })
    },
    resetDialogForm () {
      this.dialogForm = { id: null, customerId: null, customerName: '', developer: '', manager: '', giftTypeIdList: [] }
    },
    handleSubmit () {
      this.$refs.dialogFormRef.validate(valid => {
        if (!valid) return
        this.submitting = true
        const isEdit = !!this.dialogForm.id

        if (isEdit) {
          giftRecipientApi.update(this.dialogForm).then(() => {
            this.$message.success('编辑成功')
            this.dialogVisible = false
            this.fetchData()
          }).catch(err => {
            console.error(err)
            this.$message.error('保存失败，请重试')
          }).finally(() => {
            this.submitting = false
          })
          return
        }

        // 新建时：先进行重合客户检测（规则：姓名包含/一致 + 开发人相同 + 业务员相同）
        giftRecipientApi.checkDuplicate(this.dialogForm).then(res => {
          const d = res.data || {}
          if (d.hasDuplicate) {
            const matchedName = d.matchedName
            const matchedDev = d.developer || '无'
            const matchedMgr = d.manager || '无'
            const matchedId = d.matchedId
            const curName = this.dialogForm.customerName
            const conflictType = d.conflictType || 'gift_dedupe'

            if (conflictType === 'gift_dedupe') {
              // 礼品名单已存在（去重）
              this.$confirm(
                `<div style="line-height: 1.7;">
                  检测到客户 <strong style="color:#e6a23c;">【${matchedName}】</strong> 已在礼品名单中（开发人：<strong>${matchedDev}</strong>，客户负责人：<strong>${matchedMgr}</strong>）。<br/><br/>
                  <strong>该客户已存在，无需重复录入。</strong><br/>
                  如需用当前填写的信息更新该客户档案，请点击【覆盖更新】；否则请点击【取消】保持原样。
                </div>`,
                '发现已存在该客户',
                {
                  dangerouslyUseHTMLString: true,
                  distinguishCancelAndClose: true,
                  confirmButtonText: '覆盖更新',
                  cancelButtonText: '取消',
                  type: 'warning'
                }
              ).then(() => {
                // 用户选择覆盖更新已有记录
                this.doSaveItem({
                  ...this.dialogForm,
                  mergeTargetId: matchedId,
                  matchedName: matchedName,
                  conflictType: 'gift_dedupe',
                  action: 'overwrite'
                })
              }).catch(() => {
                // 用户点击取消或关闭弹窗：放弃添加，保持原样，绝不创建重复记录
                this.submitting = false
              })
            } else {
              // 跨模块合称
              this.$confirm(
                `<div style="line-height: 1.7;">
                  检测到在【客户管理】中已有合称客户 <strong style="color:#409eff;">【${matchedName}】</strong>（开发人：<strong>${matchedDev}</strong>，业务员：<strong>${matchedMgr}</strong>）。<br/><br/>
                  本次录入为合称【<strong>${curName}</strong>】，请问是否将客户档案<strong>合并更新为最新合称</strong>？<br/>
                  <span style="font-size:12px; color:#909399;">
                    ・ 点击【合并为合称】：同步更新档案名称为最新合称【${curName}】<br/>
                    ・ 点击【独立保存】：按原样新建一条独立的礼品名单记录
                  </span>
                </div>`,
                '发现客户管理合称档案',
                {
                  dangerouslyUseHTMLString: true,
                  distinguishCancelAndClose: true,
                  confirmButtonText: '合并为合称',
                  cancelButtonText: '独立保存',
                  type: 'info'
                }
              ).then(() => {
                // 用户选择合并为合称
                this.doSaveItem({
                  ...this.dialogForm,
                  mergeTargetId: matchedId,
                  matchedName: matchedName,
                  conflictType: 'customer_align',
                  action: 'merge'
                })
              }).catch(action => {
                if (action === 'cancel') {
                  // 用户选择独立保存
                  this.doSaveItem({
                    ...this.dialogForm,
                    conflictType: 'customer_align',
                    action: 'create'
                  })
                } else {
                  this.submitting = false
                }
              })
            }
          } else {
            // 无重合，正常新建
            this.doSaveItem(this.dialogForm)
          }
        }).catch(err => {
          console.error(err)
          this.doSaveItem(this.dialogForm)
        })
      })
    },
    doSaveItem (payload) {
      giftRecipientApi.add(payload).then(res => {
        const d = res.data || {}
        this.$message.success(d.message || '新建成功')
        this.dialogVisible = false
        this.fetchData()
      }).catch(err => {
        console.error(err)
        this.$message.error('保存失败，请重试')
      }).finally(() => {
        this.submitting = false
      })
    },

    // 删除单条
    handleDelete (row) {
      this.$confirm(`确定要删除客户【${row.customerName}】的礼品名单记录吗？`, '删除确认', {
        type: 'warning',
        confirmButtonText: '确定删除',
        cancelButtonText: '取消'
      }).then(() => {
        giftRecipientApi.delete(row.id).then(() => {
          this.$message.success('删除成功')
          if (this.list.length === 1 && this.page > 1) this.page--
          this.fetchData()
        }).catch(() => {
          this.$message.error('删除失败')
        })
      }).catch(() => {})
    },

    // 批量删除
    handleBatchDelete () {
      const ids = this.selectedRows.map(r => r.id)
      if (!ids.length) return
      this.$confirm(`确定要批量删除已选中的 ${ids.length} 条名单记录吗？`, '批量删除确认', {
        type: 'warning',
        confirmButtonText: '确定删除',
        cancelButtonText: '取消'
      }).then(() => {
        giftRecipientApi.batchDelete(ids).then(() => {
          this.$message.success('批量删除成功')
          this.fetchData()
        }).catch(() => {
          this.$message.error('批量删除失败')
        })
      }).catch(() => {})
    },

    // 导出 Excel
    handleExport () {
      this.exporting = true
      const params = Object.assign({
        name: this.searchName.trim() || undefined,
        developer: this.searchDeveloper.trim() || undefined,
        manager: this.searchManager.trim() || undefined,
        giftTypeId: this.searchGiftTypeId || undefined,
        dormantStatus: (this.searchDormantStatus !== null && this.searchDormantStatus !== undefined && this.searchDormantStatus !== '') ? this.searchDormantStatus : undefined
      }, this.scopeParams())
      if (this.selectedRows.length) {
        params.ids = this.selectedRows.map(r => r.id).join(',')
      }
      giftRecipientApi.export(params).then(res => {
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = '礼品名单.xlsx'
        link.click()
        URL.revokeObjectURL(link.href)
        this.$message.success('导出成功')
      }).catch(err => {
        console.error(err)
        this.$message.error('导出失败')
      }).finally(() => {
        this.exporting = false
      })
    },

    // 导入相关
    openImportDialog () {
      this.importFile = null
      this.importDialogVisible = true
    },
    resetImport () {
      this.importFile = null
      this.showDirectDetail = false
      if (this.$refs.uploadRef) this.$refs.uploadRef.clearFiles()
    },
    handleFileChange (file) {
      this.importFile = file.raw
    },
    handleFileRemove () {
      this.importFile = null
    },
    handleDownloadTemplate () {
      giftRecipientApi.downloadTemplate().then(res => {
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = '礼品名单导入模板.xlsx'
        link.click()
        URL.revokeObjectURL(link.href)
      }).catch(() => {
        this.$message.error('模板下载失败')
      })
    },
    handleDoImport () {
      if (!this.importFile) {
        this.$message.warning('请选择需要上传的 Excel 文件')
        return
      }
      this.importing = true
      giftRecipientApi.importPreview(this.importFile).then(res => {
        const d = res.data || {}
        if (!d.success) {
          this.$message.error(d.message || '文件解析失败')
          return
        }
        this.importDirectList = d.directList || []

        // 1. 礼品名单去重列表（默认 action 为 skip 无需导入）
        const dedupes = d.giftDedupeList || (d.conflicts || []).filter(c => c.conflictType === 'gift_dedupe')
        this.giftDedupeList = dedupes.map(c => ({
          ...c,
          action: c.action || 'skip'
        }))

        // 2. 客户管理合称对齐列表（默认 action 为 merge 合并为合称）
        const aligns = d.customerAlignList || (d.conflicts || []).filter(c => c.conflictType === 'customer_align')
        this.customerAlignList = aligns.map(c => ({
          ...c,
          action: c.action || 'merge'
        }))

        // 步骤逻辑：先检测礼品名单有没有，先问去重！
        if (this.giftDedupeList.length > 0) {
          this.dedupeDialogVisible = true
        } else if (this.customerAlignList.length > 0) {
          // 没有去重项，但有客户管理合称项，直接问客户管理对齐
          this.alignDialogVisible = true
        } else {
          // 既无去重又无合称，直接导入完成
          this.handleFinalImport()
        }
      }).catch(err => {
        console.error(err)
        this.$message.error('导入预检失败')
      }).finally(() => {
        this.importing = false
      })
    },

    // 确认去重后，再判断和客户管理中对齐的逻辑
    handleDedupeConfirm () {
      this.dedupeDialogVisible = false
      if (this.customerAlignList.length > 0) {
        this.alignDialogVisible = true
      } else {
        this.handleFinalImport()
      }
    },

    setAllDedupeAction (act) {
      this.giftDedupeList.forEach(c => { c.action = act })
    },
    setAllAlignAction (act) {
      this.customerAlignList.forEach(c => { c.action = act })
    },

    handleFinalImport () {
      this.importConfirming = true
      const allResolutions = [...this.giftDedupeList, ...this.customerAlignList]

      // 检查是否有任何实际需要新增或更新的内容
      const hasDirect = this.importDirectList && this.importDirectList.length > 0
      const hasModifiedResolution = allResolutions.some(r => r.action !== 'skip')

      if (!hasDirect && !hasModifiedResolution) {
        // 全选无需导入，且没有其他待导入数据
        this.dedupeDialogVisible = false
        this.alignDialogVisible = false
        this.importDialogVisible = false
        this.importFile = null
        if (this.$refs.uploadRef) this.$refs.uploadRef.clearFiles()
        this.importConfirming = false
        const skipCount = allResolutions.length
        this.$message.info(skipCount > 0 ? `已选择【无需导入】，${skipCount} 位重复客户已全部跳过，原档案保持不变` : '未选择任何导入项，已取消导入')
        return
      }

      giftRecipientApi.importConfirm({
        directList: this.importDirectList,
        conflictResolutions: allResolutions
      }).then(res => {
        const d = res.data || {}
        if (d.success) {
          const isAllSkipped = (d.createdCount === 0 && d.mergedCount === 0)
          if (isAllSkipped) {
            this.$message.info(d.message || `已选择【无需导入】，重复客户已全部跳过，原档案保持不变`)
          } else {
            const directCount = (this.importDirectList || []).length
            const skipCount = allResolutions.filter(r => r.action === 'skip').length
            const overwriteCount = allResolutions.filter(r => r.action === 'overwrite' || r.action === 'merge').length

            let tip = d.message
            if (directCount > 0) {
              tip = `导入成功！已正常导入 ${directCount} 位新客户`
              if (overwriteCount > 0) tip += `，覆盖更新 ${overwriteCount} 位已有记录`
              if (skipCount > 0) tip += `（另有 ${skipCount} 位重复客户已选择无需导入，已自动跳过）`
            }
            this.$message.success(tip || '导入成功')
          }
          this.dedupeDialogVisible = false
          this.alignDialogVisible = false
          this.importDialogVisible = false
          this.importFile = null
          if (this.$refs.uploadRef) this.$refs.uploadRef.clearFiles()
          this.fetchData()
        } else {
          this.$message.error(d.message || '导入失败')
        }
      }).catch(err => {
        console.error(err)
        this.$message.error('导入提交失败')
      }).finally(() => {
        this.importConfirming = false
      })
    },
    handleUnfreeze (row) {
      this.$confirm(`确认对客户「${row.customerName}」解除礼品休眠状态？解禁后该客户可重新申报各类礼品。`, '解除礼品休眠确认', {
        confirmButtonText: '确定解禁',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        giftRecipientApi.unfreeze(row.id).then(res => {
          if (res.data && res.data.success !== false) {
            this.$message.success(res.data.message || `客户「${row.customerName}」已解除礼品休眠`)
            this.fetchData()
          } else {
            this.$message.error((res.data && res.data.message) || '解禁失败')
          }
        }).catch(err => {
          this.$message.error('操作失败：' + (err.message || '网络错误'))
        })
      }).catch(() => {})
    },
    handleBatchUnfreeze () {
      if (!this.selectedDormantRows.length) return
      const names = this.selectedDormantRows.map(r => r.customerName).slice(0, 5).join('、')
      const extra = this.selectedDormantRows.length > 5 ? ` 等共 ${this.selectedDormantRows.length} 位` : ''
      this.$confirm(`确认对选中的「${names}」${extra}客户批量解除礼品休眠？解禁后客户可重新申报各类礼品。`, '批量解除休眠确认', {
        confirmButtonText: '确定解禁',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const ids = this.selectedDormantRows.map(r => r.id)
        giftRecipientApi.batchUnfreeze(ids).then(res => {
          if (res.data && res.data.success !== false) {
            this.$message.success(res.data.message || '批量解除休眠成功')
            this.fetchData()
          } else {
            this.$message.error((res.data && res.data.message) || '批量解禁失败')
          }
        }).catch(err => {
          this.$message.error('操作失败：' + (err.message || '网络错误'))
        })
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.gift-recipient-page {
  padding: 4px;
}
.card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.card-head .title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}
.head-tools {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.search-row-wrap {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
}
.search-bar {
  margin-bottom: 0;
}
.search-row-right {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}
.pagination-wrap {
  margin-top: 16px;
  text-align: right;
}
.muted {
  color: #909399;
}
.action-btn-group {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  flex-wrap: nowrap;
}
.action-btn-group .el-button {
  margin-left: 12px;
}
.action-btn-group .el-button:first-child {
  margin-left: 0;
}
</style>
